/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { CacheService } from '@core/cache/cache.service';
import { User } from '@core/user/user.types';
import { EnvironmentService } from '@core/utils/environment/environment.service';
import { DEFAULT_HTTP_OPTIONS_NO_EXPIRE, HttpOptions } from '@core/utils/http-options.types';
import { environment } from '@env/environment';
import { TranslocoService } from '@jsverse/transloco';
import { getAuth, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import * as moment from 'moment-timezone';
import { catchError, firstValueFrom, map, Observable, of, ReplaySubject, tap } from 'rxjs';


/**
 * Service for managing user data and authentication.
 */
@Injectable({
    providedIn: 'root'
})
export class UserService {
    /**
     * ReplaySubject that emits the current FirebaseUser object.
     */
    private _user: ReplaySubject<FirebaseUser> = new ReplaySubject<FirebaseUser>(1);

    /**
     * AuthService instance used for signing out the user.
     */
    private _authService: AuthService;

    /**
     * User object for Qart API.
     */
    public qartUser: User;

    /**
     * The cache namespace.
     */
    private _cacheNamespace: string = 'user';

    /**
     * Constructor
     *
     * @param _environmentService EnvironmentService instance for getting API URL.
     * * @param _translocoService - The TranslocoService instance to use for internationalization.
     * @param _httpClient HttpClient instance for making HTTP requests.
     * @param _cacheService CacheService instance for caching data.
     */
    constructor(
        private _environmentService: EnvironmentService,
        private _translocoService: TranslocoService,
        private _httpClient: HttpClient,
        private _cacheService: CacheService
    ) {
        const auth = getAuth();
        onAuthStateChanged(auth, (user) => {
            if (user) {
                this._user.next(user);
                if (window["dataLayer"]) {
                    window["dataLayer"].push({
                        user_id: user.uid,
                        user_email: user.email,
                        user_name: user.displayName,
                        user_phone_number: user.phoneNumber,
                        user_avatar_url: user.photoURL
                    });
                }
            } else if (this._authService?.check()) {
                //this._authService.signOut();
                this._user.next(null);
            } else {
                this._user.next(null);
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter for the user property.
     * @param value - The FirebaseUser object to set as the user.
     */
    set user(value: FirebaseUser) {
        // Store the value
        this._user.next(value);
    }

    /**
     * Returns an observable of the current user.
     * @returns An observable of type FirebaseUser.
     */
    get user$(): Observable<FirebaseUser> {
        return this._user.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sets the authentication service for the user service.
     * @param authService - The authentication service to set.
     */
    setAuthService(authService: AuthService): void {
        this._authService = authService;
    }

    /**
     * Resets the user state by setting the current user to null and clearing the qartUser property.
     * @returns void
     */
    reset(): void {
        this._user.next(null);
        this.qartUser = null;
    }

    /**
     * Retrieves the Qart user object for the current FirebaseUser object.
     * @param errorIfNotExist Whether to throw an error if the user does not exist in the Qart API.
     * @param options - The options related to cache and propagation.
     * @returns A Promise that resolves to the Qart user object.
     */
    async getQartUser(errorIfNotExist: boolean = true, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Promise<User> {
        if (this.qartUser) {
            return this.qartUser;
        } 
        // Makes sure that the user is logged in
        const user: FirebaseUser = await firstValueFrom(this._user.asObservable());
        // If the user is already loaded, return it
        const cacheParams: any = { user };
        if (options.cache && this._cacheService.has(this._cacheNamespace, 'user', cacheParams)) {
            this.qartUser = this._cacheService.get(this._cacheNamespace, 'user', cacheParams) as User;    
            return this.qartUser;
        }
        // Otherwise, load the user from the backend
        try {
            this.qartUser = await firstValueFrom(
                this._httpClient.get(
                    `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/users/${errorIfNotExist ? '1' : '0'}`,
                    {
                        headers: new HttpHeaders().set('Content-Type', 'application/json'),
                        withCredentials: true
                    })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        console.error(`Error: ${error}`);
                        return of(null);
                    }),
                    // Handle the logic of the cache and the propagation
                    tap((qartUser: User) => {
                        if (options.cache) {
                            this._cacheService.set(this._cacheNamespace, 'user', cacheParams, qartUser, options.expire);
                        }
                    })
                )) as User;
            return this.qartUser;
        }
        catch (error) {
            return null;
        }
    }

    /**
     * Creates a new user object in the Qart API for the current FirebaseUser object.
     * @param options - The options related to cache and propagation.
     * @returns A Promise that resolves to the newly created Qart user object.
     */
    async createUser(options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Promise<any> {
        const user: FirebaseUser = await firstValueFrom(this._user.asObservable());
        if (!user) {
            return null;
        }
        const currentLanguage: string = this._translocoService.getActiveLang();
        const newUser: User = {
            uid: user.uid,
            name: user.displayName,
            email: user.email,
            emailVerified: user.emailVerified,
            timezone: moment.tz.guess(true),
            photoUrl: user.photoURL,
            phoneNumber: user.phoneNumber,
            providerId: user.providerId,
            preferredLanguage: currentLanguage
        };
        try {
            this.qartUser = await firstValueFrom(this._httpClient.post(
                `${environment.qart.apiUrl}/${environment.qart.apiVersion}/users/${user.uid}`,
                { newUser },
                {
                    headers: new HttpHeaders().set('Content-Type', 'application/json'),
                    withCredentials: true
                })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error(`Error: ${error}`);
                    return of(null);
                }),
                // Handle the logic of the cache and the propagation
                tap((qartUser: User) => {
                    if (options.cache) {
                        this._cacheService.set(this._cacheNamespace, 'user', { user }, qartUser, options.expire);
                    }
                })
            )) as User;
            return this.qartUser;
        }
        catch (error) {
            return null;
        }
    }

    /**
     * Updates the user object in the Qart API for the current FirebaseUser object.
     * @param options - The options related to cache and propagation.
     * @returns A Promise that resolves to the updated Qart user object.
     */
    async updateUser(options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Promise<any> {
        const user: FirebaseUser = await firstValueFrom(this._user.asObservable());
        if (!user) {
            return null;
        }
        const currentLanguage: string = this._translocoService.getActiveLang();
        const newUser: User = {
            uid: user.uid,
            name: user.displayName,
            email: user.email,
            emailVerified: user.emailVerified,
            timezone: moment.tz.guess(true),
            photoUrl: user.photoURL,
            phoneNumber: user.phoneNumber,
            providerId: user.providerId,
            preferredLanguage: currentLanguage
        };
        try {
            this.qartUser = await firstValueFrom(this._httpClient.put(
                `${environment.qart.apiUrl}/${environment.qart.apiVersion}/users/${user.uid}`,
                { newUser },
                {
                    headers: new HttpHeaders().set('Content-Type', 'application/json'),
                    withCredentials: true
                })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error(`Error: ${error}`);
                    return of(null);
                }),
                // Handle the logic of the cache and the propagation
                tap((qartUser: User) => {
                    if (options.cache) {
                        this._cacheService.set(this._cacheNamespace, 'user', { user }, qartUser, options.expire);
                    }
                })
            )) as User;
            return this.qartUser;
        }
        catch (error) {
            return null;
        }
    }

    /**
     * Returns an observable that emits the country code of the user's IP address.
     * @param options - The options related to cache and propagation.
     * @returns An observable that emits a string representing the country code.
     */
    getCountryCode(options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<string> {
        // If the country code is already loaded, return it
        const cacheParams: any = {};
        if (options.cache && this._cacheService.has(this._cacheNamespace, 'user-country-code', cacheParams)) {
            const countryCode: string = this._cacheService.get(this._cacheNamespace, 'user-country-code', cacheParams) as string;    
            return of(countryCode);
        }
        // Otherwise, load the user from the backend
        return this._httpClient.get(`https://ipapi.co/json`)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.log('Error:', error);
                    return of(null);
                }),
                map((response: any) => response && response['country_code'] ? response['country_code'].toLowerCase() : null),
                // Handle the logic of the cache and the propagation
                tap((countryCode: string) => {
                    if (options.cache) {
                        this._cacheService.set(this._cacheNamespace, 'user-country-code', cacheParams, countryCode, options.expire);
                    }
                })
            );
    }

}
